import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../lib/api';

interface FavoriteSession { visitorToken:string; vehicleIds:number[] }
interface FavoriteResult { liked:boolean; count:number }
interface LocalVehicleValue { favorites:number[]; compare:number[]; likeCounts:Record<number,number>; toggleFavorite:(id:number)=>void; toggleCompare:(id:number)=>void; clearCompare:()=>void }
const LocalVehicleContext=createContext<LocalVehicleValue|null>(null);
const read=(key:string)=>{try{return JSON.parse(localStorage.getItem(key)??'[]') as number[]}catch{return[]}};

export function LocalVehicleProvider({children}:{children:ReactNode}){
  const [favorites,setFavorites]=useState<number[]>(()=>read('gangacars:favorites'));
  const [compare,setCompare]=useState<number[]>(()=>read('gangacars:compare'));
  const [likeCounts,setLikeCounts]=useState<Record<number,number>>({});
  const favoriteTouched=useRef(false);
  const session=useRef<Promise<string>|null>(null);
  const favoriteSync=useRef<Promise<void>>(Promise.resolve());
  const update=(key:string,setter:typeof setFavorites,fn:(items:number[])=>number[])=>setter(items=>{const next=fn(items);localStorage.setItem(key,JSON.stringify(next));return next});
  const getSession=useCallback(()=>{
    if(!session.current){
      const visitorToken=localStorage.getItem('gangacars:visitor-token')??undefined;
      const legacyVisitorId=localStorage.getItem('gangacars:visitor-id')??undefined;
      session.current=api<FavoriteSession>('/api/favorites/session',{method:'POST',body:JSON.stringify({visitorToken,legacyVisitorId})}).then(data=>{
        localStorage.setItem('gangacars:visitor-token',data.visitorToken);
        localStorage.removeItem('gangacars:visitor-id');
        if(!favoriteTouched.current){setFavorites(data.vehicleIds);localStorage.setItem('gangacars:favorites',JSON.stringify(data.vehicleIds))}
        return data.visitorToken;
      }).catch(error=>{session.current=null;throw error});
    }
    return session.current;
  },[]);
  useEffect(()=>{void getSession().catch(error=>console.error('No se pudo iniciar la sesión de favoritos.',error))},[getSession]);
  const toggleFavorite=useCallback((id:number)=>{
    const shouldLike=!favorites.includes(id);
    favoriteTouched.current=true;
    update('gangacars:favorites',setFavorites,items=>shouldLike?[...items.filter(item=>item!==id),id]:items.filter(item=>item!==id));
    favoriteSync.current=favoriteSync.current.then(async()=>{
      const visitorToken=await getSession();
      const result=await api<FavoriteResult>(`/api/likes/${id}`,{method:shouldLike?'POST':'DELETE',body:JSON.stringify({visitorToken})});
      setLikeCounts(current=>({...current,[id]:result.count}));
    }).catch(error=>{
      console.error('No se pudo actualizar el favorito.',error);
      update('gangacars:favorites',setFavorites,items=>shouldLike?items.filter(item=>item!==id):[...items.filter(item=>item!==id),id]);
    });
  },[favorites,getSession]);
  const toggleCompare=useCallback((id:number)=>update('gangacars:compare',setCompare,items=>items.includes(id)?items.filter(item=>item!==id):items.length<3?[...items,id]:items),[]);
  const clearCompare=useCallback(()=>update('gangacars:compare',setCompare,()=>[]),[]);
  const value=useMemo(()=>({favorites,compare,likeCounts,toggleFavorite,toggleCompare,clearCompare}),[favorites,compare,likeCounts,toggleFavorite,toggleCompare,clearCompare]);
  return <LocalVehicleContext.Provider value={value}>{children}</LocalVehicleContext.Provider>
}
export const useLocalVehicles=()=>{const value=useContext(LocalVehicleContext);if(!value)throw new Error('LocalVehicleProvider missing');return value};
